import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  Icon,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@saviynt/design-system';

import {
  ACC_CERT_ENTITLEMENT_VALUEKEY,
  COL_FILTER_HEADER_ACCOUNT,
  COL_FILTER_HEADER_CHILD_ENTITLEMENT,
  COL_FILTER_HEADER_ENTITLEMENT,
  COL_FILTER_HEADER_ROLE,
  COL_FILTER_HEADER_USER,
  ENT_CERT_ENTITLEMENT_VALUEKEY,
  EO_ACC_CERT_ENTITLEMENT_VALUEKEY,
  EO_CHILD_CERT_ENTITLEMENT_VALUEKEY,
  EO_ENT_CERT_ENTITLEMENT_VALUEKEY,
  EO_OBJECTS_TO_BE_INCLUDED_ACCOUNTS,
  EO_OBJECTS_TO_BE_INCLUDED_CHILD_ENTITLEMENTS,
  EO_OBJECTS_TO_BE_INCLUDED_ENTITLEMENTS,
  OBJECTS_TO_BE_INCLUDED_ACCOUNTS,
  OBJECTS_TO_BE_INCLUDED_ENTITLEMENTS,
  OBJECTS_TO_BE_INCLUDED_ROLES,
  OBJECTS_TO_BE_INCLUDED_USERS,
  RLE_CERT_ENTITLEMENT_VALUEKEY,
  USR_CERT_ENTITLEMENT_VALUEKEY,
} from '../../../components/ViewControls';
import { certsExportColumnsToExport } from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
} from '../../../utils/constant';
import { getMessageKey } from '../../../utils/utils';

import AddToExport from './AddToExport';
import { EO_DEFAULT_PROPERTIES, mapColumnData } from './CertsExportsUtils';
import IncludedInExport from './IncludedInExport';

function SelectAndOrganizeYourData() {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [selection, setSelection] = useState(1);
  const [allColumnsData, setAllColumnsData] = useState({});

  const { messages } = useSelector((state) => state?.user) || {};
  const {
    certsExport: { applyFilter },
    global: { columnPickerList, viewRowsList },
    viewControls: { rowsFilters },
    core: { campaignType },
  } = useSelector((state) => state?.certifications?.details);

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const showUsers =
    viewRowsList?.includes(OBJECTS_TO_BE_INCLUDED_USERS) &&
    (!applyFilter ||
      !rowsFilters ||
      rowsFilters?.includes(USR_CERT_ENTITLEMENT_VALUEKEY));
  const showRoles =
    viewRowsList?.includes(OBJECTS_TO_BE_INCLUDED_ROLES) &&
    (!applyFilter ||
      !rowsFilters ||
      rowsFilters?.includes(RLE_CERT_ENTITLEMENT_VALUEKEY));
  let showAccounts =
    viewRowsList?.includes(OBJECTS_TO_BE_INCLUDED_ACCOUNTS) &&
    (!applyFilter ||
      !rowsFilters ||
      rowsFilters?.includes(ACC_CERT_ENTITLEMENT_VALUEKEY));

  let showEntitlements =
    viewRowsList?.includes(OBJECTS_TO_BE_INCLUDED_ENTITLEMENTS) &&
    (!applyFilter ||
      !rowsFilters ||
      rowsFilters?.includes(ENT_CERT_ENTITLEMENT_VALUEKEY));
  const showChildEntitlements =
    viewRowsList?.includes(EO_OBJECTS_TO_BE_INCLUDED_CHILD_ENTITLEMENTS) &&
    (!applyFilter ||
      !rowsFilters ||
      rowsFilters?.includes(EO_CHILD_CERT_ENTITLEMENT_VALUEKEY));

  // Campaign
  if (isEOCampaign) {
    showAccounts =
      viewRowsList?.includes(EO_OBJECTS_TO_BE_INCLUDED_ACCOUNTS) &&
      (!applyFilter ||
        !rowsFilters ||
        rowsFilters?.includes(EO_ACC_CERT_ENTITLEMENT_VALUEKEY));
    showEntitlements =
      viewRowsList?.includes(EO_OBJECTS_TO_BE_INCLUDED_ENTITLEMENTS) &&
      (!applyFilter ||
        !rowsFilters ||
        rowsFilters?.includes(EO_ENT_CERT_ENTITLEMENT_VALUEKEY));
  }

  const availableColumnObject = () => {
    const arr = [];

    if (isEOCampaign) {
      // EO campaign order
      if (showEntitlements) arr.push(COL_FILTER_HEADER_ENTITLEMENT);
      if (showAccounts) arr.push(COL_FILTER_HEADER_ACCOUNT);
      if (showChildEntitlements) arr.push(COL_FILTER_HEADER_CHILD_ENTITLEMENT);

      return arr;
    }

    if (showUsers) arr.push(COL_FILTER_HEADER_USER);
    if (showAccounts) arr.push(COL_FILTER_HEADER_ACCOUNT);
    if (showEntitlements) arr.push(COL_FILTER_HEADER_ENTITLEMENT);
    if (showRoles) arr.push(COL_FILTER_HEADER_ROLE);
    if (showChildEntitlements) arr.push(COL_FILTER_HEADER_CHILD_ENTITLEMENT);

    return arr;
  };

  const availableObjects = availableColumnObject();

  const selectedTab =
    availableObjects.length && availableObjects[selection - 1];

  useEffect(() => {
    if (!columnPickerList) return;

    const transformColumns = (columnList) =>
      Object.entries(columnList).reduce((acc, [key, columns]) => {
        const enhancedColumns = isEOCampaign
          ? { ...columns, ...EO_DEFAULT_PROPERTIES[key] }
          : columns;

        acc[key] = mapColumnData(
          isEOCampaign,
          Object.values(enhancedColumns),
          messages,
          intl,
          key
        );

        return acc;
      }, {});

    const allColumnsWithLabels = transformColumns(columnPickerList);

    setAllColumnsData(allColumnsWithLabels);
  }, [columnPickerList, messages]);

  useEffect(() => {
    const columnsToExport = {};

    const addFilteredColumns = (key, shouldShow) => {
      if (shouldShow && allColumnsData?.[key]) {
        columnsToExport[key] = allColumnsData[key]
          .filter((column) => column.isChecked)
          .map((column) => column.id.split('.')[0]);
      }
    };

    if (isEOCampaign) {
      addFilteredColumns(COL_FILTER_HEADER_ENTITLEMENT, showEntitlements);
      addFilteredColumns(COL_FILTER_HEADER_ACCOUNT, showAccounts);
      addFilteredColumns(
        COL_FILTER_HEADER_CHILD_ENTITLEMENT,
        showChildEntitlements
      );
    } else {
      addFilteredColumns(COL_FILTER_HEADER_USER, showUsers);
      addFilteredColumns(COL_FILTER_HEADER_ACCOUNT, showAccounts);
      addFilteredColumns(COL_FILTER_HEADER_ENTITLEMENT, showEntitlements);
      addFilteredColumns(COL_FILTER_HEADER_ROLE, showRoles);
    }

    dispatch(certsExportColumnsToExport(columnsToExport));
  }, [allColumnsData, applyFilter]);

  const title =
    messages.CERTS_EXPORT_SELECT_AND_ORGANIZE_YOUR_DATA ||
    localize(intl, getMessageKey('CERTS_EXPORT_SELECT_AND_ORGANIZE_YOUR_DATA'));
  const userLabel =
    messages['User_DISPLAYNAME.CERTIFICATIONS'] ||
    localize(intl, getMessageKey('User_DISPLAYNAME.CERTIFICATIONS'));
  const roleLabel =
    messages['Role_DISPLAYNAME.CERTIFICATIONS'] ||
    localize(intl, getMessageKey('Role_DISPLAYNAME.CERTIFICATIONS'));
  const accountLabel =
    messages['Account_DISPLAYNAME.CERTIFICATIONS'] ||
    localize(intl, getMessageKey('Account_DISPLAYNAME.CERTIFICATIONS'));
  const entitlementLabel =
    messages['Ent_DISPLAYNAME.CERTIFICATIONS'] ||
    localize(intl, getMessageKey('Ent_DISPLAYNAME.CERTIFICATIONS'));
  const childEntitlementLabel =
    messages['ChildEnt_DISPLAYNAME.CERTIFICATIONS'] ||
    localize(intl, getMessageKey('ChildEnt_DISPLAYNAME.CERTIFICATIONS'));

  return (
    <Box className='SelectAndOrganizeYourData'>
      <Typography kind='h3'>{title}</Typography>

      <Box className='SelectAndOrganizeYourData-tabs'>
        {isEOCampaign ? (
          <ToggleButtonGroup selection={selection} setSelection={setSelection}>
            {showEntitlements && (
              <ToggleButton
                label={entitlementLabel}
                key={entitlementLabel}
                icon={
                  <Icon
                    className='InputField-suffixButton-icon'
                    kind='Entitlement'
                    color='navy-700'
                    size='smallMedium'
                  />
                }
              />
            )}
            {showAccounts && (
              <ToggleButton
                label={accountLabel}
                key={accountLabel}
                icon={
                  <Icon
                    className='InputField-suffixButton-icon'
                    kind='Account'
                    color='navy-700'
                    size='smallMedium'
                  />
                }
              />
            )}
            {showChildEntitlements && (
              <ToggleButton
                label={childEntitlementLabel}
                key={childEntitlementLabel}
                icon={
                  <Icon
                    className='InputField-suffixButton-icon'
                    kind='Account'
                    color='navy-700'
                    size='smallMedium'
                  />
                }
              />
            )}
          </ToggleButtonGroup>
        ) : (
          <ToggleButtonGroup selection={selection} setSelection={setSelection}>
            {showUsers && (
              <ToggleButton
                label={userLabel}
                icon={
                  <Icon
                    className='InputField-suffixButton-icon'
                    kind='UserBg'
                    size='smallMedium'
                  />
                }
              />
            )}
            {showAccounts && (
              <ToggleButton
                label={accountLabel}
                icon={
                  <Icon
                    className='InputField-suffixButton-icon'
                    kind='Account'
                    color='navy-700'
                    size='smallMedium'
                  />
                }
              />
            )}

            {showEntitlements && (
              <ToggleButton
                label={entitlementLabel}
                icon={
                  <Icon
                    className='InputField-suffixButton-icon'
                    kind='Entitlement'
                    color='navy-700'
                    size='smallMedium'
                  />
                }
              />
            )}
            {showRoles && (
              <ToggleButton
                label={roleLabel}
                icon={
                  <Icon
                    className='InputField-suffixButton-icon'
                    kind='Role'
                    color='navy-700'
                    size='smallMedium'
                  />
                }
              />
            )}
          </ToggleButtonGroup>
        )}
      </Box>

      <Box className='SelectAndOrganizeYourData-tabsContent'>
        <IncludedInExport
          allColumnsData={allColumnsData}
          setAllColumnsData={setAllColumnsData}
          selectedTab={selectedTab}
        />
        <AddToExport
          allColumnsData={allColumnsData}
          setAllColumnsData={setAllColumnsData}
          selectedTab={selectedTab}
        />
      </Box>
    </Box>
  );
}

export default SelectAndOrganizeYourData;
